import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { supabase } from '../supabaseClient';
import '../styles/portal.css';

const BUCKET = 'data-room';

const SECTIONS = [
  { key: 'Documents', label: 'Documents', allowView: true },
  { key: 'Reports', label: 'Performance Reports', allowView: true },
  { key: 'Data', label: 'Data', allowView: false },
];

// List every file in a bucket folder, paging until exhausted
async function listAllInFolder(folder) {
  const pageSize = 100;
  let offset = 0;
  const items = [];
  while (true) {
    const { data, error } = await supabase.storage.from(BUCKET).list(folder, {
      limit: pageSize,
      offset,
      sortBy: { column: 'updated_at', order: 'desc' },
    });
    if (error) throw error;
    if (!data || data.length === 0) break;
    for (const entry of data) {
      if (entry.name) items.push(entry);
    }
    if (data.length < pageSize) break;
    offset += pageSize;
  }
  return items;
}

async function signedUrl(path, expirySeconds = 600) {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expirySeconds);
  if (error) throw error;
  return data.signedUrl;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return '';
  }
}

export default function InvestorsPortal() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState({});
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  // account dropdown
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on click outside / Escape
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Current user's email for the dropdown (RequireAuth guards access)
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (active) setUserEmail(data?.user?.email || '');
    })();
    return () => { active = false; };
  }, []);

  // Load all sections with signed URLs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const results = await Promise.all(
          SECTIONS.map(async (s) => {
            const files = await listAllInFolder(s.key);
            const withUrls = await Promise.all(
              files.map(async (f) => {
                const path = `${s.key}/${f.name}`;
                const url = await signedUrl(path, 600);
                return {
                  path,
                  name: f.name,
                  url,
                  updated_at: f.updated_at,
                  size: f.metadata?.size ?? null,
                };
              })
            );
            return [s.key, withUrls];
          })
        );

        if (!cancelled) setSections(Object.fromEntries(results));
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e.message || 'Failed to load documents.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/investor-login', { replace: true });
  };

  const handleRefresh = () => window.location.reload();

  const handleDownload = async (path, name) => {
    try {
      // Fetch the file as a Blob using the user's auth
      const { data, error } = await supabase.storage.from(BUCKET).download(path);
      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Revoke after a tick to avoid cutting off the download
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      console.error('Download failed, falling back to signed URL…', e);
      // Fallback: mint a signed URL with ?download= to force attachment
      try {
        const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, 600);
        const fallbackUrl = `${data.signedUrl}${
          data.signedUrl.includes('?') ? '&' : '?'
        }download=${encodeURIComponent(name)}`;
        window.location.href = fallbackUrl;
      } catch (e2) {
        console.error('Fallback signed URL failed:', e2);
        alert('Sorry, the download could not be started.');
      }
    }
  };

  return (
    <div className="page page--overlay">
      <Header size="small" />

      <main className="portal-body">
        {/* Compact top bar with email dropdown */}
        <div className="portal-topbar">
          <div className="portal-overline">Data Room</div>

          {userEmail && (
            <div className={`dropdown ${menuOpen ? 'open' : ''}`} ref={menuRef}>
              <button
                className="dropdown-trigger"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
                title={userEmail}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{userEmail}</span>
                <svg className="chev" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"/>
                </svg>
              </button>

              {menuOpen && (
                <div className="dropdown-menu" role="menu">
                  <button className="menu-item" role="menuitem" onClick={() => { handleRefresh(); setMenuOpen(false); }}>
                    Refresh
                  </button>
                  <button className="menu-item danger" role="menuitem" onClick={() => { handleLogout(); setMenuOpen(false); }}>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className="portal-banner portal-banner--error">{error}</div>}

        {SECTIONS.map((sec) => {
          const files = sections[sec.key] || [];
          const isLoading = loading && !sections[sec.key];
          const count = files.length;

          return (
            <section className="portal-section" key={sec.key}>
              <div className="section-header">
                <div className="section-title">{sec.label}</div>
                <div className="section-meta">
                  {isLoading ? 'Loading…' : count === 0 ? 'No files' : `${count} file${count > 1 ? 's' : ''}`}
                </div>
              </div>

              {isLoading ? (
                <div className="file-list">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div className="row-skel" key={i}>
                      <div className="skel" style={{ width: '50%' }} />
                      <div className="skel" style={{ width: 120, height: 28, justifySelf: 'end' }} />
                    </div>
                  ))}
                </div>
              ) : count > 0 ? (
                <div className="file-list">
                  {files.map((f) => (
                    <div className="file-row" key={f.path}>
                      <div className="file-info">
                        <div className="file-name">{f.name}</div>
                        <div className="file-meta">
                          {f.size ? `${(f.size / 1024 / 1024).toFixed(2)} MB · ` : ''}
                          {f.updated_at ? `Updated ${fmtDate(f.updated_at)}` : ''}
                        </div>
                      </div>
                      <div className="file-actions">
                        {sec.allowView && (
                          <a className="link" href={f.url} target="_blank" rel="noreferrer">
                            View
                          </a>
                        )}
                        <button
                          type="button"
                          className="link"
                          onClick={() => handleDownload(f.path, f.name)}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="file-list">
                  <div className="file-row--empty">Nothing here yet.</div>
                </div>
              )}
            </section>
          );
        })}

        <div className="portal-footnote">
          Having trouble opening a file? Try “Refresh”, or get in touch with our site admin
          - dev@erebus.capital
        </div>
      </main>
    </div>
  );
}
